import React, { useEffect, useCallback, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Box, Alert, Typography, Drawer, IconButton, useMediaQuery, useTheme } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { connect } from 'react-redux';

// Context and Hooks
import { LiveStudioProvider, useLiveStudio } from './context/LiveStudioContext';
import { useRoomConnection } from './hooks/useRoomConnection';
import { useMediaControls } from './hooks/useMediaControls';
import { useLiveStreaming } from './hooks/useLiveStreaming';
import { useChat } from './hooks/useChat';
import { useErrorHandler } from './hooks/useErrorHandler';
import { useVideoRefs } from './hooks/useVideoRefs';

// Components
import { ErrorBoundary } from './components/ErrorBoundary';
import { Header } from './components/Header';
import { VideoGrid } from './components/VideoGrid';
import { ControlBar } from './components/ControlBar';
import { ChatPanel } from './components/ChatPanel';
import { ParticipantsPanel } from './components/ParticipantsPanel';
import { CommentsPanel } from './components/CommentsPanel';
import ChannelSettingsDrawer from './components/ChannelSettingsDrawer';
import { DeviceSelector } from './components/DeviceSelector';
import { LeaveConfirmationDialog } from './components/LeaveConfirmationDialog';
import { PreJoinScreen } from './components/PreJoinScreen';
import { LoadingScreen } from './components/LoadingScreen';
import { ErrorScreen } from './components/ErrorScreen';
import ChannelControl from '../Channel/ChannelControl';
import StreamTargetDialogWidget from '../StreamTargetDialog/StreamTargetDialogWidget';

// Types
import { LiveStudioProps } from './types';
import { SIDEBAR_WIDTH } from './constants';

// Main Live Studio Component
const LiveStudioContent: React.FC<{
  events: any;
  roomState: any;
  connectionState: any;
  connectToRoom: any;
  disconnectFromRoom: any;
  updateParticipants: any;
  attachVideoTrack: any
}> = ({
  events,
  roomState: hookRoomState,
  connectionState: hookConnectionState,
  connectToRoom,
  disconnectFromRoom,
  updateParticipants,
  attachVideoTrack
}) => {
  const { eventCode } = useParams<{ eventCode: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isTablet = useMediaQuery(theme.breakpoints.between('md', 'lg'));

  // Get event title from events data (events is a mapping of [eventCode]: {})
  const currentEvent = events?.[eventCode || ''];
  const eventTitle = currentEvent?.title || currentEvent?.name || eventCode;

  const {
    roomState,
    liveStreamState,
    uiControlsState,
    connectionState,
    connectionQualityInfo,
    videoRefs,
    profile,
    deviceSelectionState,
    setShowChat,
    setShowParticipants,
    setShowSettings,
    setShowComments,
    setShowChannelControl,
    setShowLeaveConfirmation,
    setRoomName,
    setError,
    setStreamError,
    setParticipants,
    setConnectionQualityInfo,
    switchCamera,
    switchMicrophone,
    refreshDevices,
  } = useLiveStudio();

  const { setError: setGlobalError, clearError } = useErrorHandler();

  // Ref to track if we've already attempted connection
  const hasAttemptedConnection = useRef(false);

  // Media controls hook
  const { toggleVideo, toggleAudio } = useMediaControls({
    localParticipant: hookRoomState.localParticipant,
    room: hookRoomState.room,
    onParticipantsUpdate: (participants) => {
      // Update participants in context
      setParticipants(participants);
    },
  });

  // Live streaming hook
  const {
    liveStreamState: hookLiveStreamState,
    startLiveStream,
    stopLiveStream,
    clearStreamError
  } = useLiveStreaming({
    profile: profile || { token: '', code: '' },
    room: hookRoomState.room,
    localParticipant: hookRoomState.localParticipant,
    roomId: eventCode || '',
    liveEvent: currentEvent,
    onError: setStreamError,
    setConnectionQualityInfo: setConnectionQualityInfo,
  });

  // Chat hook
  const {
    chatMessages,
    newMessage,
    setNewMessage,
    sendMessage,
    handleDataReceived,
    clearMessages,
  } = useChat({
    room: hookRoomState.room,
  });


  // Handle room connection
  const handleConnectToRoom = useCallback(async (roomId: string) => {
    await connectToRoom(roomId);
  }, [connectToRoom]);

  // Handle disconnect
  const handleDisconnectFromRoom = useCallback(async () => {
    // Stop live stream if active
    if (hookLiveStreamState.isLiveStreaming && hookLiveStreamState.activeEgressId) {
      try {
        await stopLiveStream();
      } catch (error) {
        console.error('Error stopping live stream on disconnect:', error);
      }
    }

    await disconnectFromRoom();
    clearMessages();
    setShowLeaveConfirmation(false);

    // Navigate back to events
    setTimeout(() => navigate('/events'), 0);
  }, [
    hookLiveStreamState.isLiveStreaming,
    hookLiveStreamState.activeEgressId,
    stopLiveStream,
    disconnectFromRoom,
    clearMessages,
    setShowLeaveConfirmation,
    navigate,
  ]);

  // Handle join room from pre-join screen
  const handleJoinRoom = useCallback(() => {
    if (roomState.roomName.trim()) {
      navigate(`/live-studio/sample-event/${roomState.roomName.trim()}`);
    }
  }, [roomState.roomName, navigate]);

  // Handle retry
  const handleRetry = useCallback(() => {
    clearError();
    setError(null);
    hasAttemptedConnection.current = false;
    // Reset to pre-join state
    // Note: setShowPreJoin would need to be added to the context
  }, [clearError, setError]);

  // Handle permission request
  const handlePermissionRequest = useCallback(async () => {
    try {
      // Request camera and microphone permissions
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'user' },
        audio: true
      });

      // Stop the stream immediately as we only want to request permissions
      stream.getTracks().forEach(track => track.stop());

      // Clear any permission-related errors
      clearError();
      setError(null);
      hasAttemptedConnection.current = false;

      // Retry connection after permissions are granted
      if (eventCode) {
        console.log('Permissions granted, retrying connection...');
        setTimeout(() => {
          connectToRoom(eventCode);
        }, 500); // Small delay to ensure permissions are fully processed
      }
    } catch (error: any) {
      console.error('Permission request failed:', error);

      // Handle different types of permission errors
      if (error.name === 'NotAllowedError') {
        // User has previously denied permissions - show specific guidance
        setError('Camera and microphone permissions have been denied. Please click the camera/microphone icon in your browser\'s address bar and allow permissions, then refresh the page.');
      } else if (error.name === 'NotFoundError') {
        // No camera/microphone found
        setError('No camera or microphone found. Please connect a camera and microphone and try again.');
      } else if (error.name === 'NotReadableError') {
        // Device is already in use
        setError('Camera or microphone is already in use by another application. Please close other applications and try again.');
      } else {
        // Generic permission error
        setError('Failed to access camera and microphone. Please check your browser permissions and try again.');
      }
    }
  }, [clearError, setError, eventCode, connectToRoom]);

  // Auto-connect if roomId is provided
  useEffect(() => {
    console.log('Auto-connect check:', {
      roomId: !!eventCode,
      showPreJoin: hookRoomState.showPreJoin,
      hasConnected: hookConnectionState.hasConnected,
      hasAttemptedConnection: hasAttemptedConnection.current,
      shouldConnect: eventCode && hookRoomState.showPreJoin && !hookConnectionState.hasConnected && !hasAttemptedConnection.current
    });

    if (eventCode && hookRoomState.showPreJoin && !hookConnectionState.hasConnected && !hasAttemptedConnection.current) {
      console.log('Starting auto-connect for room:', eventCode);
      hasAttemptedConnection.current = true;
      setRoomName(eventCode);
      console.log('Calling connectToRoom with roomId:', eventCode);
      connectToRoom(eventCode);
    }
  }, [eventCode, hookRoomState.showPreJoin, hookConnectionState.hasConnected, setRoomName, connectToRoom]);

  // Monitor local participant changes
  useEffect(() => {
    console.log('Local participant state changed:', {
      hasLocalParticipant: !!hookRoomState.localParticipant,
      localParticipantSid: hookRoomState.localParticipant?.sid,
      localParticipantIdentity: hookRoomState.localParticipant?.identity,
      localParticipantName: hookRoomState.localParticipant?.name,
      isCameraEnabled: hookRoomState.localParticipant?.isCameraEnabled,
      isMicrophoneEnabled: hookRoomState.localParticipant?.isMicrophoneEnabled
    });
  }, [hookRoomState.localParticipant]);

  // Connection timeout detection
  useEffect(() => {
    if (hookRoomState.room && !hookRoomState.localParticipant && !hookRoomState.isConnecting && !hookRoomState.error) {
      console.log('Connection timeout detected - room exists but no local participant');
      const timeoutTimer = setTimeout(() => {
        console.log('Connection timeout - setting error');
        setError('Connection timeout: Room connected but local participant not available');
      }, 10000); // 10 second timeout

      return () => {
        clearTimeout(timeoutTimer);
      };
    }
  }, [hookRoomState.room, hookRoomState.localParticipant, hookRoomState.isConnecting, hookRoomState.error, setError]);

  // Setup room event listeners for chat
  useEffect(() => {
    if (hookRoomState.room) {
      const room = hookRoomState.room;

      const handleDataReceivedEvent = (payload: Uint8Array, participant?: any) => {
        handleDataReceived(payload, participant);
      };

      room.on('dataReceived', handleDataReceivedEvent);

      return () => {
        room.off('dataReceived', handleDataReceivedEvent);
      };
    }
  }, [hookRoomState.room, handleDataReceived]);

  // Handle local video track when room is connected
  useEffect(() => {
    if (hookRoomState.room && hookRoomState.localParticipant) {
      console.log('Setting up local video track listener');

      // Check if local participant already has video tracks
      const videoPublications = Array.from(hookRoomState.localParticipant.videoTrackPublications.values());
      console.log('Local video publications:', videoPublications.length);

      videoPublications.forEach((publication: any) => {
        if (publication.track) {
          console.log('Attaching existing local video track');
          // Use requestAnimationFrame to ensure DOM is ready
          requestAnimationFrame(() => {
            attachVideoTrack(publication.track!, hookRoomState.localParticipant!.sid, true);
          });
        }
      });

      // Listen for new video tracks
      const handleLocalTrackPublished = (publication: any) => {
        console.log('Local track published:', {
          kind: publication.kind,
          source: publication.source,
          sid: publication.trackSid
        });

        if (publication.kind === 'video' && publication.track) {
          // Use requestAnimationFrame to ensure DOM is ready
          requestAnimationFrame(() => {
            attachVideoTrack(publication.track, hookRoomState.localParticipant!.sid, true);
          });
        }
      };

      hookRoomState.localParticipant.on('trackPublished', handleLocalTrackPublished);

      return () => {
        console.log('Cleaning up local track listener');
        hookRoomState.localParticipant?.off('trackPublished', handleLocalTrackPublished);
      };
    }
  }, [hookRoomState.room, hookRoomState.localParticipant, attachVideoTrack]);

  // Handle page leave scenarios (browser close, refresh, navigation)
  useEffect(() => {
    // Track if we should show confirmation for system shortcuts
    let shouldShowConfirmation = false;

    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      // Only show confirmation if we're connected to a room and should show confirmation
      if (hookRoomState.room && hookRoomState.localParticipant && shouldShowConfirmation) {
        event.preventDefault();
        event.returnValue = ''; // Required for the dialog to appear
        return ''; // Required for other browsers
      }
    };

    const handlePopState = (event: PopStateEvent) => {
      // Handle browser back/forward navigation
      if (hookRoomState.room && hookRoomState.localParticipant) {
        event.preventDefault();
        setShowLeaveConfirmation(true);
        // Push the current state back to prevent navigation
        window.history.pushState(null, '', window.location.href);
      }
    };

    // Handle keyboard shortcuts (Ctrl+R, F5, Ctrl+W, etc.)
    const handleKeyDown = (event: KeyboardEvent) => {
      if (hookRoomState.room && hookRoomState.localParticipant) {
        // Debug logging to see what keys are being pressed
        console.log('Key pressed:', {
          key: event.key,
          code: event.code,
          ctrlKey: event.ctrlKey,
          metaKey: event.metaKey,
          shiftKey: event.shiftKey,
          altKey: event.altKey
        });

        // For system shortcuts that can't be prevented, show dialog immediately
        const isSystemShortcut = (
          // Cmd+W (close tab) - can't be prevented
          ((event.ctrlKey || event.metaKey) && (event.key === 'w' || event.key === 'W')) ||
          // Cmd+Q (quit app) - can't be prevented
          ((event.ctrlKey || event.metaKey) && (event.key === 'q' || event.key === 'Q')) ||
          // Cmd+Shift+W (close all tabs) - can't be prevented
          ((event.ctrlKey || event.metaKey) && event.shiftKey && (event.key === 'W' || event.key === 'w')) ||
          // Cmd+Option+W (close other tabs) - can't be prevented
          ((event.ctrlKey || event.metaKey) && event.altKey && (event.key === 'w' || event.key === 'W'))
        );

        if (isSystemShortcut) {
          // For system shortcuts, set flag and show dialog
          shouldShowConfirmation = true;
          setShowLeaveConfirmation(true);

          // Reset flag after a short delay
          setTimeout(() => {
            shouldShowConfirmation = false;
          }, 100);

          return;
        }

        // For other shortcuts that can be prevented
        // Refresh shortcuts (both Windows/Linux and Mac)
        if ((event.ctrlKey || event.metaKey) && (event.key === 'r' || event.key === 'R')) {
          event.preventDefault();
          setShowLeaveConfirmation(true);
        }

        // F5 refresh
        if (event.key === 'F5' || event.code === 'F5') {
          event.preventDefault();
          setShowLeaveConfirmation(true);
        }
      }
    };

    // Handle mobile pull-to-refresh prevention
    const handleTouchStart = (event: TouchEvent) => {
      if (hookRoomState.room && hookRoomState.localParticipant) {
        // Store initial touch position
        const touch = event.touches[0];
        if (touch) {
          (window as any).initialTouchY = touch.clientY;
          (window as any).initialTouchX = touch.clientX;
        }
      }
    };

    const handleTouchMove = (event: TouchEvent) => {
      if (hookRoomState.room && hookRoomState.localParticipant) {
        const touch = event.touches[0];
        if (touch && (window as any).initialTouchY !== undefined) {
          const deltaY = touch.clientY - (window as any).initialTouchY;
          const deltaX = touch.clientX - (window as any).initialTouchX;

          // Check if user is pulling down from the top of the page
          if (deltaY > 0 && touch.clientY < 100 && Math.abs(deltaX) < Math.abs(deltaY)) {
            // Prevent pull-to-refresh
            event.preventDefault();
            setShowLeaveConfirmation(true);
          }
        }
      }
    };

    const handleTouchEnd = () => {
      // Clean up touch tracking
      delete (window as any).initialTouchY;
      delete (window as any).initialTouchX;
    };

    // Add event listeners
    window.addEventListener('beforeunload', handleBeforeUnload);
    window.addEventListener('popstate', handlePopState);
    document.addEventListener('keydown', handleKeyDown);
    document.addEventListener('touchstart', handleTouchStart, { passive: false });
    document.addEventListener('touchmove', handleTouchMove, { passive: false });
    document.addEventListener('touchend', handleTouchEnd);

    // Push initial state to enable popstate detection
    if (hookRoomState.room && hookRoomState.localParticipant) {
      window.history.pushState(null, '', window.location.href);
    }

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      window.removeEventListener('popstate', handlePopState);
      document.removeEventListener('keydown', handleKeyDown);
      document.removeEventListener('touchstart', handleTouchStart);
      document.removeEventListener('touchmove', handleTouchMove);
      document.removeEventListener('touchend', handleTouchEnd);
    };
  }, [hookRoomState.room, hookRoomState.localParticipant, hookLiveStreamState.isLiveStreaming, setShowLeaveConfirmation]);

  // Handle page visibility change (when user switches tabs or minimizes)
  useEffect(() => {
    const handleVisibilityChange = () => {
      // Only show warning if user switches tabs while actively streaming
      if (document.hidden && hookRoomState.room && hookRoomState.localParticipant && hookLiveStreamState.isLiveStreaming) {
        // User switched tabs or minimized while streaming - show warning dialog
        setShowLeaveConfirmation(true);
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [hookRoomState.room, hookRoomState.localParticipant, hookLiveStreamState.isLiveStreaming, setShowLeaveConfirmation]);

  // Cleanup on unmount - stop live stream when leaving
  useEffect(() => {
    return () => {
      hasAttemptedConnection.current = false;
      // Auto-stop live stream when component unmounts
      if (hookLiveStreamState.isLiveStreaming) {
        console.log('Auto-stopping live stream on unmount...');
        stopLiveStream().catch(error => {
          console.error('Error auto-stopping live stream on unmount:', error);
        });
      }
    };
  }, [hookLiveStreamState.isLiveStreaming, stopLiveStream]);

  // Auto-start live streaming when room is ready
  useEffect(() => {
    const autoStartStream = async () => {
      // Only auto-start if:
      // 1. Room is connected
      // 2. Local participant is available
      // 3. Not already streaming
      // 4. Not currently starting/stopping
      if (
        hookRoomState?.room?.state === 'connected' &&
        hookRoomState?.localParticipant &&
        !hookLiveStreamState.isLiveStreaming &&
        !hookLiveStreamState.isStartingStream &&
        !hookLiveStreamState.isStoppingStream
      ) {
        console.log('Auto-starting live stream...');
        try {
          await startLiveStream();
        } catch (error) {
          console.error('Auto-start failed:', error);
        }
      }
    };

    // Add a small delay to ensure everything is properly initialized
    const timer = setTimeout(autoStartStream, 2000);
    return () => clearTimeout(timer);
  }, [
    hookRoomState?.room?.state,
    hookRoomState?.localParticipant,
    hookLiveStreamState.isLiveStreaming,
    hookLiveStreamState.isStartingStream,
    hookLiveStreamState.isStoppingStream,
    startLiveStream
  ]);

  // Debug logging
  try {
    console.log('LiveStudioPage render state:', {
      roomId: eventCode,
      showPreJoin: hookRoomState?.showPreJoin,
      isConnecting: hookRoomState?.isConnecting,
      hasError: !!hookRoomState?.error,
      errorMessage: hookRoomState?.error,
      hasRoom: !!hookRoomState?.room,
      roomState: hookRoomState?.room?.state,
      hasLocalParticipant: !!hookRoomState?.localParticipant,
      localParticipantSid: hookRoomState?.localParticipant?.sid,
      localParticipantIdentity: hookRoomState?.localParticipant?.identity,
      localParticipantName: hookRoomState?.localParticipant?.name,
      participantsCount: hookRoomState?.participants?.length || 0,
      hasAttemptedConnection: hasAttemptedConnection.current,
      connectionState: hookConnectionState,
      hasVideoRefs: !!videoRefs,
      hasVideoRefsLocal: !!videoRefs?.local,
      hasVideoRefsRemote: !!videoRefs?.remote
    });
  } catch (error) {
    console.error('Error in debug logging:', error);
  }

  // Render different screens based on state
  if (hookRoomState.showPreJoin && !eventCode) {
    return (
      <PreJoinScreen
        roomName={hookRoomState.roomName}
        onRoomNameChange={setRoomName}
        onJoinRoom={handleJoinRoom}
        error={hookRoomState.error}
        onClearError={() => setError(null)}
      />
    );
  }

  if (hookRoomState.isConnecting) {
    return <LoadingScreen />;
  }

  if (hookRoomState.error) {
    return <ErrorScreen error={hookRoomState.error} onRetry={handleRetry} onPermissionRequest={handlePermissionRequest} />;
  }

  // Responsive sidebar width
  const getSidebarWidth = () => {
    if (isMobile) return '100vw';
    if (isTablet) return 400;
    return 600;
  };

  return (
    <Box
      height="100vh"
      display="flex"
      flexDirection="column"
      sx={{
        backgroundColor: theme.palette.background.default,
        overflow: 'hidden'
      }}
    >
      <style>
        {`
          @keyframes pulse {
            0% { opacity: 1; }
            50% { opacity: 0.5; }
            100% { opacity: 1; }
          }

          /* Mobile optimizations */
          @media (max-width: 768px) {
            .MuiDrawer-paper {
              width: 100vw !important;
              max-width: 100vw !important;
            }
          }

          /* Smooth transitions */
          .video-container {
            transition: all 0.3s ease-in-out;
          }

          .sidebar-container {
            transition: all 0.3s ease-in-out;
          }

          /* Prevent pull-to-refresh on mobile */
          body {
            overscroll-behavior-y: contain;
            -webkit-overflow-scrolling: touch;
          }

          /* Additional mobile touch prevention */
          @media (max-width: 768px) {
            body {
              touch-action: pan-x pan-y;
              -webkit-touch-callout: none;
              -webkit-user-select: none;
              -khtml-user-select: none;
              -moz-user-select: none;
              -ms-user-select: none;
              user-select: none;
            }
          }
        `}
      </style>

      {/* Header */}
      <Header
        roomName={eventTitle}
        roomId={eventCode || ''}
        room={hookRoomState.room}
        liveStreamState={hookLiveStreamState}
        uiControlsState={uiControlsState}
        connectionQualityInfo={connectionQualityInfo}
        onToggleSettings={() => setShowSettings(!uiControlsState.showSettings)}
        onToggleComments={() => setShowComments(!uiControlsState.showComments)}
        onToggleChannelControl={() => setShowChannelControl(!uiControlsState.showChannelControl)}
        onLeaveRoom={() => setShowLeaveConfirmation(true)}
      />

      {/* Main Content */}
      <Box
        display="flex"
        flexGrow={1}
        overflow="hidden"
        sx={{
          flexDirection: isMobile ? 'column' : 'row',
          height: 'calc(100vh - 64px)', // Account for header height
        }}
      >
        {/* Video Area */}
        <Box
          flexGrow={1}
          display="flex"
          flexDirection="column"
          p={isMobile ? 0.5 : 1}
          className="video-container"
          sx={{
            minHeight: isMobile ? '60vh' : 'auto',
            maxHeight: isMobile ? '60vh' : 'none',
            overflow: isMobile ? 'hidden' : 'visible',
          }}
        >

          {/* Video Grid */}
          {(() => {
            try {
              if (videoRefs && videoRefs.local && videoRefs.remote) {
                return (
                  <VideoGrid
                    participants={hookRoomState.participants}
                    localParticipant={hookRoomState.localParticipant}
                    videoRefs={videoRefs}
                    onToggleVideo={toggleVideo}
                    onToggleAudio={toggleAudio}
                  />
                );
              } else {
                return (
                  <Box
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    height="100%"
                    sx={{ bgcolor: theme.palette.background.paper }}
                  >
                    <Typography color="textSecondary">
                      Initializing video components...
                    </Typography>
                  </Box>
                );
              }
            } catch (error) {
              console.error('Error rendering VideoGrid:', error);
              return (
                <Box
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  height="100%"
                  sx={{ bgcolor: theme.palette.error.main + '20' }}
                >
                  <Typography color="error">
                    Error loading video: {error instanceof Error ? error.message : 'Unknown error'}
                  </Typography>
                </Box>
              );
            }
          })()}

          {/* Stream Error Display */}
          {hookLiveStreamState.streamError && (
            <Box p={2}>
              <Alert severity="error" onClose={clearStreamError}>
                {hookLiveStreamState.streamError}
              </Alert>
            </Box>
          )}

          {/* Device Selection - Right under video */}
          <Box
            p={isMobile ? 0.5 : 1}
            display="flex"
            justifyContent="center"
            sx={{
              backgroundColor: theme.palette.background.paper,
              borderRadius: isMobile ? 0 : 1,
              boxShadow: isMobile ? 'none' : `0 2px 8px ${theme.palette.common.black}20`,
              margin: isMobile ? 0 : 1,
            }}
          >
            <DeviceSelector
              deviceState={deviceSelectionState}
              onCameraChange={switchCamera}
              onMicrophoneChange={switchMicrophone}
              onRefresh={refreshDevices}
              compact={true}
              isLiveStreaming={hookLiveStreamState.isLiveStreaming}
            />
          </Box>

          {/* Channel Control - Stream Destinations (now in drawer) */}

          {/* Control Bar */}
          <ControlBar
            localParticipant={hookRoomState.localParticipant}
            room={hookRoomState.room}
            liveStreamState={hookLiveStreamState}
            onToggleVideo={toggleVideo}
            onToggleAudio={toggleAudio}
            onStartLiveStream={startLiveStream}
          />

        </Box>

        {/* Sidebar - Comments */}
        {uiControlsState.showComments && (
          <Box
            width={getSidebarWidth()}
            borderLeft={isMobile ? "none" : `1px solid ${theme.palette.divider || 'rgba(255, 255, 255, 0.1)'}`}
            display="flex"
            flexDirection="column"
            className="sidebar-container"
            sx={{
              backgroundColor: theme.palette.background.paper,
              height: isMobile ? '40vh' : '100%',
              minHeight: isMobile ? '40vh' : 'auto',
              maxHeight: isMobile ? '40vh' : 'none',
              overflow: 'hidden',
              boxShadow: isMobile ? `0 -2px 8px ${theme.palette.common.black}20` : `0 2px 8px ${theme.palette.common.black}20`,
              borderRadius: isMobile ? '16px 16px 0 0' : 0,
              position: isMobile ? 'relative' : 'static',
              zIndex: isMobile ? 1000 : 'auto',
            }}
          >
            <CommentsPanel liveEvent={currentEvent} />
          </Box>
        )}
      </Box>


      {/* Channel Control Panel */}
      <Drawer
        anchor={isMobile ? "bottom" : "right"}
        open={uiControlsState.showChannelControl}
        onClose={() => setShowChannelControl(false)}
        sx={{
          '& .MuiDrawer-paper': {
            width: isMobile ? '100vw' : 550,
            maxWidth: isMobile ? '100vw' : '90vw',
            height: isMobile ? '80vh' : '100vh',
            padding: isMobile ? 2 : 3,
            boxSizing: 'border-box',
            borderRadius: isMobile ? '16px 16px 0 0' : 0,
            backgroundColor: theme.palette.background.paper,
          },
        }}
      >
        {profile && currentEvent && (
          <Box p={2}>
            <Typography variant="h6" gutterBottom>
              {t('Stream on Social Networks')}
            </Typography>
            <ChannelControl
              liveEvent={currentEvent}
              setError={setStreamError}
            />
          </Box>
        )}
      </Drawer>

      {/* Channel Settings Panel */}
      <Drawer
        anchor={isMobile ? "bottom" : "right"}
        open={uiControlsState.showSettings}
        onClose={() => setShowSettings(false)}
        sx={{
          '& .MuiDrawer-paper': {
            width: isMobile ? '100vw' : 500,
            maxWidth: isMobile ? '100vw' : '90vw',
            height: isMobile ? '80vh' : '100vh',
            padding: isMobile ? 2 : 3,
            boxSizing: 'border-box',
            borderRadius: isMobile ? '16px 16px 0 0' : 0,
            backgroundColor: theme.palette.background.paper,
          },
        }}
      >
        {profile && currentEvent && (
          <ChannelSettingsDrawer
            liveEvent={currentEvent}
          />
        )}
      </Drawer>

      {/* Leave Room Confirmation Dialog */}
      <LeaveConfirmationDialog
        open={roomState.showLeaveConfirmation}
        onClose={() => setShowLeaveConfirmation(false)}
        onConfirm={handleDisconnectFromRoom}
      />

      {/* Stream Target Dialog Widget */}
      <StreamTargetDialogWidget />
    </Box>
  );
};

// Main component with provider
const LiveStudioPage: React.FC<LiveStudioProps> = ({ profile, events }) => {
  // Create video refs for the room connection
  const videoRefs = useVideoRefs();

  // Room connection hook - moved outside of context
  const {
    roomState: hookRoomState,
    connectionState: hookConnectionState,
    connectToRoom,
    disconnectFromRoom,
    updateParticipants,
    attachVideoTrack,
  } = useRoomConnection({
    profile: profile || { token: '', code: '' },
    videoRefs,
    onParticipantsUpdate: (participants) => {
      // This will be handled by the context
    },
    onError: (error) => {
      // This will be handled by the context
    },
  });

  return (
    <ErrorBoundary>
      <LiveStudioProvider
        profile={profile}
        events={events}
        roomState={{
          room: hookRoomState.room,
          localParticipant: hookRoomState.localParticipant,
        }}
        videoRefs={videoRefs}
      >
        <LiveStudioContent
          events={events}
          roomState={hookRoomState}
          connectionState={hookConnectionState}
          connectToRoom={connectToRoom}
          disconnectFromRoom={disconnectFromRoom}
          updateParticipants={updateParticipants}
          attachVideoTrack={attachVideoTrack}
        />
      </LiveStudioProvider>
    </ErrorBoundary>
  );
};

// Redux connection
const mapStateToProps = function (state: any) {
  return {
    events: state.events,
    profile: state.user.profile,
  };
};

export default connect(mapStateToProps)(LiveStudioPage);

